import React, { useState, useMemo, useCallback, type FC, type Key } from 'react'
import { Drawer } from 'antd'

import { useDeleteRelationshipsMutation } from '../../../state/apis/generated/relationshipsApi'
import { operationDrawerProps, showNotification, parsePermissionRowKey } from '../../../constants/helpers/common'
import { type IUserPermission, type IDrawerFooterBlock, type IRTKQResponseResult } from '../../../constants/types/common'
import { type IOktaUser, type IRelationshipRequest } from '../../../constants/types/generatedTypesAliases'
import { UsersAvatarBlock, DrawerFooterBlock, CloseDrawerButton } from '../../shared'
import { PermissionTable } from './PermissionTable'

interface IRemoveUserPermissionsPanelProps {
  permissions: IUserPermission[]
  permissionsLoading: boolean
  selectedUsers: IOktaUser[]
  removePermissionDrawerVisible: boolean
  setRemovePermissionDrawerVisible: (arg: boolean) => void
}

export const RemoveUserPermissionsPanel: FC<IRemoveUserPermissionsPanelProps> = ({
  permissions,
  permissionsLoading,
  selectedUsers,
  removePermissionDrawerVisible,
  setRemovePermissionDrawerVisible
}) => {
  const [removeRelationships] = useDeleteRelationshipsMutation()
  const [selectedRowKeys, setSelectedRowKeys] = useState<Key[]>([])

  const selectedPermissions = useMemo(() => (
    selectedRowKeys.map(rowKey => parsePermissionRowKey(rowKey as string))
  ), [selectedRowKeys])

  const handleClosePanel = useCallback(() => {
    setRemovePermissionDrawerVisible(false)
    setSelectedRowKeys([])
  }, [setRemovePermissionDrawerVisible])

  const handleRemovePermission = useCallback(async () => {
    const userIds = selectedUsers.map(u => u.id)
    const relationships = userIds.reduce((acc: IRelationshipRequest[], userId) => (
      acc.concat(selectedPermissions.map(r => ({
        ...r,
        resourceType: `${r.namespace}/${r.resourceType}`,
        userId })))
    ), [])

    const result: IRTKQResponseResult = await removeRelationships({
      serviceAuthorisationDomainModelsSpiceDbRequestsSpiceDbWriteRelationshipsRequest: {
        relationships
      }
    })

    showNotification({
      result,
      success: `Permissions removed from ${selectedUsers.length} user${selectedUsers.length === 1 ? '' : 's'}`
    })

    handleClosePanel()
  }, [
    selectedUsers,
    selectedPermissions,
    handleClosePanel,
    removeRelationships,
  ])

  const footerButtonsProps: IDrawerFooterBlock = useMemo(() => ({
    confirm: {
      children: 'Remove Permissions',
      disabled: !selectedRowKeys.length,
      onClick: handleRemovePermission,
    },
    cancel: {
      onClick: handleClosePanel
    }

  }), [
    selectedRowKeys.length,
    handleClosePanel,
    handleRemovePermission,
  ])

  return (
    <Drawer
      footer={(
        <DrawerFooterBlock {...footerButtonsProps} />
      )}
      extra={(
        <CloseDrawerButton onCloseDrawer={handleClosePanel} />
      )}
      {...operationDrawerProps}
      closable={false}
      title='Remove Permissions'
      data-testid='remove-permissions-drawer'
      onClose={handleClosePanel}
      open={removePermissionDrawerVisible}>
      <UsersAvatarBlock users={selectedUsers} />
      <PermissionTable
        permissions={permissions}
        loading={permissionsLoading}
        selectedRowKeys={selectedRowKeys}
        setSelectedRowKeys={setSelectedRowKeys}
      />
    </Drawer>
  )
}
